// src/pages/AdminUsersPage.tsx
import { motion } from "framer-motion";
import { Shield, Users, Building2, GitBranch } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  if (!user?.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950">
        <div className="text-center">
          <Shield className="h-20 w-20 text-red-500 mx-auto mb-6" />
          <h1 className="text-5xl font-bold text-white mb-4">Доступ запрещён</h1>
          <p className="text-purple-300 text-xl">Эта страница доступна только Администратору</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 p-6 md:p-10">
      <div className="max-w-full mx-auto">

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-4 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl px-10 py-5 mb-6">
            <Shield className="h-12 w-12 text-purple-400" />
            <h1 className="text-5xl font-bold text-white tracking-tighter">Панель Администратора</h1>
          </div>
          <p className="text-purple-300 text-2xl">Добро пожаловать, {user?.name?.split(" ")[0]}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Управление аккаунтами */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            onClick={() => navigate("/admin/accounts")}
            className="group bg-gradient-to-br from-slate-900 to-purple-950/60 border border-purple-700/50 rounded-3xl p-10 cursor-pointer hover:border-purple-500 transition-all duration-300 h-full"
          >
            <div className="flex items-center gap-6 mb-8">
              <div className="w-20 h-20 bg-purple-600/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Users className="h-12 w-12 text-purple-400" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Управление аккаунтами</h2>
                <p className="text-purple-300 text-lg leading-relaxed">
                  Список пользователей, смена паролей, управление ролями
                </p>
              </div>
            </div>
            <div className="text-purple-400 group-hover:text-purple-300 transition-colors text-lg font-medium flex items-center gap-2 mt-auto">
              Открыть модуль →
            </div>
          </motion.div>

          {/* Управление организациями */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            onClick={() => navigate("/admin/organizations")}
            className="group bg-gradient-to-br from-slate-900 to-purple-950/60 border border-purple-700/50 rounded-3xl p-10 cursor-pointer hover:border-purple-500 transition-all duration-300 h-full"
          >
            <div className="flex items-center gap-6 mb-8">
              <div className="w-20 h-20 bg-emerald-600/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Building2 className="h-12 w-12 text-emerald-400" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Управление организациями</h2>
                <p className="text-purple-300 text-lg leading-relaxed">
                  Создание и редактирование организаций
                </p>
              </div>
            </div>
            <div className="text-emerald-400 group-hover:text-emerald-300 transition-colors text-lg font-medium flex items-center gap-2 mt-auto">
              Открыть модуль →
            </div>
          </motion.div>

          {/* Редактирование структуры */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            onClick={() => navigate("/admin/structure")}
            className="group bg-gradient-to-br from-slate-900 to-purple-950/60 border border-purple-700/50 rounded-3xl p-10 cursor-pointer hover:border-purple-500 transition-all duration-300 h-full"
          >
            <div className="flex items-center gap-6 mb-8">
              <div className="w-20 h-20 bg-amber-600/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <GitBranch className="h-12 w-12 text-amber-400" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Структура организаций</h2>
                <p className="text-purple-300 text-lg leading-relaxed">
                  Редактирование иерархии должностей
                </p>
              </div>
            </div>
            <div className="text-amber-400 group-hover:text-amber-300 transition-colors text-lg font-medium flex items-center gap-2 mt-auto">
              Открыть модуль →
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
}